<template>
  <div class="main-monitor">
    <div class="search-bar" id="searchInput">
      <input
        id="tipinput"
        v-model="searchQuery"
        type="text"
        placeholder="搜索位置"
        @keyup.enter="searchLocation"
      />
      <button @click="searchLocation">搜索</button>
      <button @click="clearSearch">取消</button>
    </div>
    <div ref="monitorMapRef" class="monitor-map"></div>
    <div class="add-prj" :class="adding ? 'blueContainer' : ''" @click="addPrj">新建<br />项目</div>
    <Avatar></Avatar>
    <div v-show="showStep" class="addPrj-steps">
      <button class="close-btn" @click="closeSteps">x</button>
      <h3 class="addPrj-title">操作步骤：</h3>
      <ol class="addPrj-list">
        <li>在地图中找到项目位置后单击鼠标左键</li>
        <li>系统会自动打开新建项目弹窗，填写项目信息</li>
      </ol>
    </div>
    <el-dialog title="是否绘制项目范围" v-model="dialogVisible" width="30%" @close="handleClose">
      <span>在地图上依次点击将要框选的范围，鼠标右键结束绘制并自动保存当前轮廓。</span>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">跳过绘制</el-button>
          <el-button type="primary" @click="drawPolygon(insertId)">开始绘制</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import ApiKeys from '@/config/ApiKeys.json'
import { ref, onMounted, onBeforeUnmount, createApp } from 'vue'
import { getPrjInfo, getCoor } from '../service/PrjInfo'
import HomePrjInfo from '@/components/mapWindow/HomePrjInfo.vue'
import AddProjectInfo from '@/components/mapWindow/AddProjectInfo.vue'
import Avatar from '../components/Avatar.vue'
import { ElMessage } from 'element-plus'

declare global {
  interface Window {
    L?: any
  }
}

const monitorMapRef = ref<HTMLElement | null>(null)
const prjList = ref<any[]>([])
const coorList = ref<any[]>([])
const markerMap = new Map<number, any>()

let map: any = null
let leaflet: any = null
let infoWindow: any = null
let marker: any = null
let drawLine: any = null
let drawMarkers: any[] = []
let drawPoints: Array<[number, number]> = []

const adding = ref(false)
const showStep = ref(false)
const dialogVisible = ref(false)
const insertId = ref(0)
const latV = ref(0)
const lngV = ref(0)
const searchQuery = ref('')

const imgMap: Record<number, string> = {
  1: 'https://image.surveyexcel.com/1200w_900h_50q_dfc87a13-fe85-4173-8eb5-4df98f2028bb',
  2: 'https://image.surveyexcel.com/1200w_900h_50q_255192be-a9f7-4855-a5e7-8263d2cacf66',
  3: 'https://image.surveyexcel.com/1200w_900h_50q_81e65ccd-2768-414c-9a34-da389b7a1bcf'
}

const showDialog = (id: number, lat: number, lng: number) => {
  insertId.value = id
  latV.value = lat
  lngV.value = lng
  dialogVisible.value = true
}

const handleClose = () => {
  dialogVisible.value = false
}

const clearSearch = () => {
  searchQuery.value = ''
}

const ensureLeafletAssets = async () => {
  if (window.L) {
    return window.L
  }

  const stylesheetId = 'leaflet-stylesheet'
  if (!document.getElementById(stylesheetId)) {
    const link = document.createElement('link')
    link.id = stylesheetId
    link.rel = 'stylesheet'
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
    document.head.appendChild(link)
  }

  await new Promise<void>((resolve, reject) => {
    const scriptId = 'leaflet-script'
    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null

    if (existingScript) {
      if (window.L) {
        resolve()
        return
      }
      existingScript.addEventListener('load', () => resolve(), { once: true })
      existingScript.addEventListener('error', () => reject(new Error('Leaflet load failed')), {
        once: true
      })
      return
    }

    const script = document.createElement('script')
    script.id = scriptId
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Leaflet load failed'))
    document.body.appendChild(script)
  })

  return window.L
}

const getMarkerIcon = (type: number) =>
  leaflet.icon({
    iconUrl:
      imgMap[type] ??
      'https://image.surveyexcel.com/1200w_900h_50q_dfc87a13-fe85-4173-8eb5-4df98f2028bb',
    iconSize: [45, 45],
    iconAnchor: [22, 42],
    popupAnchor: [0, -42]
  })

const createPopup = (component: any, props: Record<string, any>, lat: number, lng: number) => {
  const tempDiv = document.createElement('div')
  const app = createApp(component, props)
  app.mount(tempDiv)

  const popup = leaflet
    .popup({
      closeButton: false,
      autoClose: true,
      closeOnClick: false,
      offset: [0, -42],
      className: 'project-popup'
    })
    .setLatLng([lat, lng])
    .setContent(tempDiv)

  popup.on('remove', () => {
    app.unmount()
  })

  return popup
}

const openProjectPopup = (item: any, lat: number, lng: number) => {
  if (!map || !leaflet) {
    return
  }

  infoWindow = createPopup(HomePrjInfo, { prjInfo: item, deletePrj }, lat, lng)
  map.openPopup(infoWindow)
}

const parseLocation = (payload: any) => {
  const parsePair = (value: any) => {
    if (typeof value === 'string') {
      const [lng, lat] = value.split(',').map((item) => Number(item))
      if (Number.isFinite(lng) && Number.isFinite(lat)) {
        return { lng, lat }
      }
    }
    return null
  }

  const candidates = [
    payload?.location,
    payload?.result?.location,
    payload?.results?.[0]?.location,
    payload?.results?.[0],
    payload?.lonlat
  ]

  for (const candidate of candidates) {
    if (!candidate) {
      continue
    }

    const pair = parsePair(candidate)
    if (pair) {
      return pair
    }

    const lng = Number(candidate.lon ?? candidate.lng ?? candidate.lnt)
    const lat = Number(candidate.lat)
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      return { lng, lat }
    }
  }

  return null
}

const searchLocation = async () => {
  if (!searchQuery.value.trim() || !map) {
    clearSearch()
    return
  }

  try {
    const response = await fetch(
      `https://api.tianditu.gov.cn/geocoder?ds=${encodeURIComponent(
        JSON.stringify({ keyWord: searchQuery.value.trim() })
      )}&tk=${ApiKeys.apiKey}`
    )
    const result = await response.json()
    const location = parseLocation(result)

    if (!location) {
      ElMessage.warning('未找到相关位置')
      return
    }

    map.setView([location.lat, location.lng], 16)
  } catch (error) {
    console.error(error)
    ElMessage.error('天地图检索失败，请稍后重试')
  }
}

const deletePrj = (id: number) => {
  prjList.value = prjList.value.filter((item: any) => item.id !== id)
  const currentMarker = markerMap.get(id)
  if (currentMarker && map) {
    map.removeLayer(currentMarker)
  }
  markerMap.delete(id)

  if (infoWindow && map) {
    map.closePopup(infoWindow)
  }

  renderMap()
}

const clearDrawDraft = () => {
  if (!map) {
    return
  }

  if (drawLine) {
    map.removeLayer(drawLine)
    drawLine = null
  }

  drawMarkers.forEach((item) => map.removeLayer(item))
  drawMarkers = []
  drawPoints = []
}

const stopPolygonDrawing = () => {
  if (!map) {
    return
  }

  map.off('click', handleDrawClick)
  map.off('contextmenu', finishDrawPolygon)
}

const handleDrawClick = (event: any) => {
  if (!map || !leaflet) {
    return
  }

  const point: [number, number] = [event.latlng.lng, event.latlng.lat]
  drawPoints.push(point)

  const dot = leaflet.circleMarker([event.latlng.lat, event.latlng.lng], {
    radius: 4,
    color: '#FF33FF',
    fillColor: '#1791fc',
    fillOpacity: 1,
    weight: 2
  })
  dot.addTo(map)
  drawMarkers.push(dot)

  const latlngs = drawPoints.map(([lng, lat]) => [lat, lng])
  if (drawLine) {
    drawLine.setLatLngs(latlngs)
  } else {
    drawLine = leaflet.polyline(latlngs, {
      color: '#FF33FF',
      weight: 3
    }).addTo(map)
  }
}

const finishDrawPolygon = () => {
  if (!map || !leaflet) {
    return
  }

  if (drawPoints.length < 3) {
    ElMessage.warning('至少需要绘制 3 个点')
    return
  }

  const polygon = leaflet.polygon(
    drawPoints.map(([lng, lat]) => [lat, lng]),
    {
      color: '#FF33FF',
      weight: 2,
      fillColor: '#1791fc',
      fillOpacity: 0.4
    }
  )

  polygon.addTo(map)
  coorList.value = [
    ...coorList.value,
    {
      project_id: insertId.value,
      coordinates: [...drawPoints]
    }
  ]

  stopPolygonDrawing()
  clearDrawDraft()
}

const newPrj = (event: any) => {
  if (!map || !leaflet) {
    return
  }

  const position = event.latlng
  if (marker) {
    map.removeLayer(marker)
  }

  marker = leaflet
    .marker([position.lat, position.lng], {
      icon: getMarkerIcon(1)
    })
    .addTo(map)

  const closeInfoWindow = () => {
    if (infoWindow && map) {
      map.closePopup(infoWindow)
    }
    if (marker && map) {
      map.removeLayer(marker)
      marker = null
    }
  }

  const addedPrj = (data: any) => {
    closeInfoWindow()
    adding.value = false
    showStep.value = false
    map.off('click', newPrj)
    prjList.value = [...prjList.value, data]
    renderMap()
  }

  infoWindow = createPopup(
    AddProjectInfo,
    {
      position,
      closeInfoWindow,
      addedPrj,
      drawPolygon,
      showDialog
    },
    position.lat,
    position.lng
  )

  map.openPopup(infoWindow)
}

const addPrj = () => {
  if (!map) {
    return
  }

  if (adding.value) {
    adding.value = false
    showStep.value = false
    map.off('click', newPrj)
  } else {
    map.on('click', newPrj)
    adding.value = true
    showStep.value = true
  }
}

const closeSteps = () => {
  showStep.value = false
}

const drawPolygon = (prjId: number) => {
  dialogVisible.value = false
  if (!map) {
    return
  }

  insertId.value = prjId
  clearDrawDraft()
  stopPolygonDrawing()
  map.setView([latV.value, lngV.value], 20)
  map.on('click', handleDrawClick)
  map.on('contextmenu', finishDrawPolygon)
  ElMessage.info('左键依次绘制范围，右键结束')
}

const renderMap = async () => {
  leaflet = await ensureLeafletAssets()
  if (!monitorMapRef.value) {
    return
  }

  if (map) {
    stopPolygonDrawing()
    clearDrawDraft()
    map.remove()
    markerMap.clear()
    map = null
  }

  map = leaflet.map(monitorMapRef.value, {
    center: [31.23, 121.47],
    zoom: 12,
    zoomControl: true
  })

  leaflet
    .tileLayer(
      `https://t{s}.tianditu.gov.cn/vec_w/wmts?service=wmts&request=GetTile&version=1.0.0&LAYER=vec&style=default&tilematrixSet=w&format=tiles&TileMatrix={z}&TileRow={y}&TileCol={x}&tk=${ApiKeys.apiKey}`,
      {
        minZoom: 1,
        maxZoom: 18,
        subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
        attribution: ''
      }
    )
    .addTo(map)

  leaflet
    .tileLayer(
      `https://t{s}.tianditu.gov.cn/cva_w/wmts?service=wmts&request=GetTile&version=1.0.0&LAYER=cva&style=default&tilematrixSet=w&format=tiles&TileMatrix={z}&TileRow={y}&TileCol={x}&tk=${ApiKeys.apiKey}`,
      {
        minZoom: 1,
        maxZoom: 18,
        subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
        attribution: ''
      }
    )
    .addTo(map)

  prjList.value.forEach((item: any) => {
    const statusMarker = leaflet
      .marker([item.lat, item.lng], {
        icon: getMarkerIcon(item.type)
      })
      .addTo(map)

    statusMarker.on('click', () => {
      openProjectPopup(item, item.lat, item.lng)
    })

    markerMap.set(item.id, statusMarker)
  })

  coorList.value.forEach((item: any) => {
    const coordinates = Array.isArray(item.coordinates)
      ? item.coordinates
      : JSON.parse(item.coordinates ?? '[]')

    if (!Array.isArray(coordinates) || !coordinates.length) {
      return
    }

    leaflet
      .polygon(
        coordinates.map((point: [number, number]) => [point[1], point[0]]),
        {
          color: '#FF33FF',
          weight: 2,
          fillColor: '#1791fc',
          fillOpacity: 0.4
        }
      )
      .addTo(map)
  })
}

onMounted(async () => {
  prjList.value = (await getPrjInfo()) ?? []
  coorList.value = (await getCoor(undefined)) ?? []
  await renderMap()
})

onBeforeUnmount(() => {
  if (!map) {
    return
  }

  stopPolygonDrawing()
  clearDrawDraft()
  map.remove()
  map = null
})
</script>

<style scoped lang="scss">
.main-monitor {
  width: 100vw;
  height: calc(100vh - 10px);

  .search-bar {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 999;
    display: flex;

    input {
      padding: 5px;
      width: 200px;
      border: 1px solid #ccc;
      border-radius: 4px;
    }

    button {
      padding: 5px 10px;
      margin-left: 10px;
      background-color: #508ef5;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
    }
  }

  .monitor-map {
    width: 100%;
    height: 100%;
  }

  .monitor-map:deep(.leaflet-container) {
    width: 100%;
    height: 100%;
    background: #eaf1f8;
  }

  .monitor-map:deep(.leaflet-control-attribution) {
    display: none;
  }

  .monitor-map:deep(.project-popup .leaflet-popup-content-wrapper) {
    background: transparent;
    box-shadow: none;
    padding: 0;
  }

  .monitor-map:deep(.project-popup .leaflet-popup-content) {
    margin: 0;
  }

  .monitor-map:deep(.project-popup .leaflet-popup-tip-container) {
    display: none;
  }

  .add-prj {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 90px;
    right: 90px;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: rgb(255, 255, 255);
    font-size: 14px;
    line-height: 16px;
    border: 1px solid #ccc;
    vertical-align: middle;
  }

  .addPrj-text {
    width: 300px;
    height: 100px;
    background-color: #fff;
    border-radius: 5px;
  }

  .addPrj-steps {
    position: absolute;
    top: 90px;
    right: 155px;
    background: #f7f7f7;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .close-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      border: none;
      background: transparent;
      cursor: pointer;
      font-size: 24px;
      line-height: 1;
      color: #aaa;

      &:hover {
        color: #777;
      }
    }

    .addPrj-title {
      font-size: 18px;
      font-weight: bold;
      color: #333;
      margin-bottom: 15px;
      text-align: center;
    }

    .addPrj-list {
      padding-left: 20px;

      li {
        margin-bottom: 10px;
        color: #666;
      }
    }
  }
}
</style>
